/// <reference lib="dom" />

import { getAccessToken } from '../auth-token'
import type { Teacher, SubstitutionRules, AvailabilityBreakdown } from '@timetable/types'

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

// ============================================================================
// Types
// ============================================================================

export interface EligibilityContext {
  targetDate: string
  periodId: string
  /** Time start of the period being covered — used for overlap-based busy check */
  periodTimeStart: string
  /** Time end of the period being covered — used for overlap-based busy check */
  periodTimeEnd: string
  dayOfWeek: string
  schoolId: string
  absentTeacherId: string
  rules: SubstitutionRules
  /** Posted + current-draft-batch assignment counts per teacher (for quota enforcement) */
  draftBatchCounts: Map<string, { daily: number; weekly: number }>
  /**
   * Time slots already assigned to each teacher in the current batch run.
   * Shared across all absence runs in a bulk operation so a teacher assigned
   * to a time slot for one absence is excluded from overlapping slots for another.
   * teacherId → Array<{ time_start: string; time_end: string }>
   */
  draftBatchAssignedPeriods?: Map<string, Array<{ time_start: string; time_end: string }>>
}

export interface EligibilityResult {
  /** Final eligible candidates after all rules applied */
  candidates: Teacher[]
  /** Breakdown of how many teachers were excluded by each rule — for progress animation */
  breakdown: AvailabilityBreakdown
}

// ============================================================================
// Helpers
// ============================================================================

function authHeaders(accessToken: string): Record<string, string> {
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  }
}

/** Returns Monday (ISO week start) for a given ISO date string */
function getWeekBounds(isoDate: string): { weekStart: string; weekEnd: string } {
  const [year, month, day] = isoDate.split('-').map(Number)
  const d = new Date(year, month - 1, day)
  const dayOfWeek = d.getDay() // 0=Sun, 1=Mon, ..., 6=Sat
  // ISO week: Monday=start, Sunday=end
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  const monday = new Date(d)
  monday.setDate(d.getDate() + diffToMonday)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)

  const fmt = (dt: Date) => dt.toISOString().split('T')[0]
  return { weekStart: fmt(monday), weekEnd: fmt(sunday) }
}

// ============================================================================
// filterEligibleCandidates
// ============================================================================

/**
 * Apply all 8 exclusion rules to filter the teacher pool for a given period.
 * Batch queries are used — no per-teacher loops.
 *
 * Returns both the eligible candidates AND a breakdown of exclusion counts
 * for the progress animation UI.
 */
export async function filterEligibleCandidates(
  teachers: Teacher[],
  context: EligibilityContext
): Promise<EligibilityResult> {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Not authenticated')

  const headers = authHeaders(accessToken)
  const {
    targetDate,
    periodTimeStart,
    periodTimeEnd,
    dayOfWeek,
    schoolId,
    absentTeacherId,
    rules,
    draftBatchCounts,
    draftBatchAssignedPeriods,
  } = context

  const totalTeachers = teachers.length

  // Rule 1: Self-exclusion (not counted in breakdown — it's always 1 and expected)
  let candidates = teachers.filter((t) => t.id !== absentTeacherId)

  if (candidates.length === 0) {
    return {
      candidates: [],
      breakdown: {
        totalTeachers,
        absentCount: 0,
        unavailableCount: 0,
        busyCount: 0,
        quotaExceededCount: 0,
        ineligibleTypeCount: 0,
        availableCount: 0,
      },
    }
  }

  // Rule 8: Eligible teacher types (apply early to reduce candidate pool)
  let ineligibleTypeCount = 0
  if (rules.eligible_teacher_type_ids && rules.eligible_teacher_type_ids.length > 0) {
    const eligibleTypeSet = new Set(rules.eligible_teacher_type_ids)
    const before = candidates.length
    candidates = candidates.filter((t) => t.teacher_type_id && eligibleTypeSet.has(t.teacher_type_id))
    ineligibleTypeCount = before - candidates.length
  }

  if (candidates.length === 0) {
    return {
      candidates: [],
      breakdown: {
        totalTeachers,
        absentCount: 0,
        unavailableCount: 0,
        busyCount: 0,
        quotaExceededCount: 0,
        ineligibleTypeCount,
        availableCount: 0,
      },
    }
  }

  // Batch fetch: absent teachers on targetDate
  const absentParams = new URLSearchParams()
  absentParams.append('select', 'teacher_id')
  absentParams.append('school_id', `eq.${schoolId}`)
  absentParams.append('start_date', `lte.${targetDate}`)
  absentParams.append('end_date', `gte.${targetDate}`)
  absentParams.append('status', `neq.cancelled`)

  const absentResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${absentParams}`, {
    headers,
  })

  const absentTeacherIds = new Set<string>()
  if (absentResponse.ok) {
    const rows: { teacher_id: string }[] = await absentResponse.json()
    for (const row of rows) absentTeacherIds.add(row.teacher_id)
  }

  // Rule 2: Absent teachers
  const beforeAbsent = candidates.length
  candidates = candidates.filter((t) => !absentTeacherIds.has(t.id))
  const absentCount = beforeAbsent - candidates.length

  if (candidates.length === 0) {
    return {
      candidates: [],
      breakdown: {
        totalTeachers,
        absentCount,
        unavailableCount: 0,
        busyCount: 0,
        quotaExceededCount: 0,
        ineligibleTypeCount,
        availableCount: 0,
      },
    }
  }

  // Batch fetch: unavailable teachers on targetDate
  const unavailParams = new URLSearchParams()
  unavailParams.append('select', 'teacher_id')
  unavailParams.append('school_id', `eq.${schoolId}`)
  unavailParams.append('start_date', `lte.${targetDate}`)
  unavailParams.append('end_date', `gte.${targetDate}`)

  const unavailResponse = await fetch(`${SUPABASE_URL}/rest/v1/unavailability?${unavailParams}`, {
    headers,
  })

  const unavailTeacherIds = new Set<string>()
  if (unavailResponse.ok) {
    const rows: { teacher_id: string }[] = await unavailResponse.json()
    for (const row of rows) unavailTeacherIds.add(row.teacher_id)
  }

  // Rule 3: Unavailable teachers
  const beforeUnavail = candidates.length
  candidates = candidates.filter((t) => !unavailTeacherIds.has(t.id))
  const unavailableCount = beforeUnavail - candidates.length

  if (candidates.length === 0) {
    return {
      candidates: [],
      breakdown: {
        totalTeachers,
        absentCount,
        unavailableCount,
        busyCount: 0,
        quotaExceededCount: 0,
        ineligibleTypeCount,
        availableCount: 0,
      },
    }
  }

  // Batch fetch: teachers busy during this period (timetable conflict)
  // Need published academic year IDs for the school
  const ayParams = new URLSearchParams()
  ayParams.append('select', 'id')
  ayParams.append('school_id', `eq.${schoolId}`)
  ayParams.append('status', `eq.published`)

  const ayResponse = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?${ayParams}`, {
    headers,
  })

  const publishedYearIds: string[] = []
  if (ayResponse.ok) {
    const rows: { id: string }[] = await ayResponse.json()
    for (const row of rows) publishedYearIds.push(row.id)
  }

  const busyTeacherIds = new Set<string>()
  if (publishedYearIds.length > 0) {
    // Fetch all timetable records for this day, joining the period's time range.
    // We then filter in-memory for time overlap because Supabase REST doesn't
    // support cross-table range comparisons in a single query.
    // A teacher is "busy" if they have any period on this day where:
    //   period.time_start < periodTimeEnd  AND  period.time_end > periodTimeStart
    const busyParams = new URLSearchParams()
    busyParams.append('select', 'teacher_id,period:schedule_periods(time_start,time_end)')
    busyParams.append('day_of_week', `eq.${dayOfWeek}`)
    busyParams.append('academic_year_id', `in.(${publishedYearIds.join(',')})`)

    const busyResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?${busyParams}`,
      { headers }
    )

    if (busyResponse.ok) {
      const rows: { teacher_id: string; period: { time_start: string; time_end: string } | null }[] =
        await busyResponse.json()
      for (const row of rows) {
        if (!row.period) continue
        // Overlap condition: starts before our period ends AND ends after our period starts
        if (row.period.time_start < periodTimeEnd && row.period.time_end > periodTimeStart) {
          busyTeacherIds.add(row.teacher_id)
        }
      }
    }
  }

  // Rule 4: Busy teachers (timetable conflict)
  const beforeBusy = candidates.length
  candidates = candidates.filter((t) => !busyTeacherIds.has(t.id))
  const busyCount = beforeBusy - candidates.length

  if (candidates.length === 0) {
    return {
      candidates: [],
      breakdown: {
        totalTeachers,
        absentCount,
        unavailableCount,
        busyCount,
        quotaExceededCount: 0,
        ineligibleTypeCount,
        availableCount: 0,
      },
    }
  }

  // Rule 4b: Already assigned to an overlapping time slot in the current batch run.
  // This prevents double-booking when multiple absences are processed together
  // and only one teacher is free at a given time slot.
  if (draftBatchAssignedPeriods && draftBatchAssignedPeriods.size > 0) {
    candidates = candidates.filter((t) => {
      const assignedSlots = draftBatchAssignedPeriods.get(t.id)
      if (!assignedSlots || assignedSlots.length === 0) return true
      // Exclude if any already-assigned slot overlaps with this period
      return !assignedSlots.some(
        (slot) => slot.time_start < periodTimeEnd && slot.time_end > periodTimeStart
      )
    })
  }

  if (candidates.length === 0) {
    return {
      candidates: [],
      breakdown: {
        totalTeachers,
        absentCount,
        unavailableCount,
        busyCount,
        quotaExceededCount: 0,
        ineligibleTypeCount,
        availableCount: 0,
      },
    }
  }

  // Batch fetch: existing posted+draft assignment counts per teacher for daily cap
  const dailyAssignParams = new URLSearchParams()
  dailyAssignParams.append('select', 'substitute_teacher_id')
  dailyAssignParams.append('date', `eq.${targetDate}`)
  dailyAssignParams.append('status', `in.(posted,draft)`)
  dailyAssignParams.append('substitute_teacher_id', `not.is.null`)

  const dailyAssignResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/assignments?${dailyAssignParams}`,
    { headers }
  )

  const dailyPostedDraftCounts = new Map<string, number>()
  if (dailyAssignResponse.ok) {
    const rows: { substitute_teacher_id: string }[] = await dailyAssignResponse.json()
    for (const row of rows) {
      const tid = row.substitute_teacher_id
      dailyPostedDraftCounts.set(tid, (dailyPostedDraftCounts.get(tid) ?? 0) + 1)
    }
  }

  // Batch fetch: weekly assignment counts
  const { weekStart, weekEnd } = getWeekBounds(targetDate)
  const weeklyAssignParams = new URLSearchParams()
  weeklyAssignParams.append('select', 'substitute_teacher_id')
  weeklyAssignParams.append('date', `gte.${weekStart}`)
  weeklyAssignParams.append('date', `lte.${weekEnd}`)
  weeklyAssignParams.append('status', `in.(posted,draft)`)
  weeklyAssignParams.append('substitute_teacher_id', `not.is.null`)

  const weeklyAssignResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/assignments?${weeklyAssignParams}`,
    { headers }
  )

  const weeklyPostedDraftCounts = new Map<string, number>()
  if (weeklyAssignResponse.ok) {
    const rows: { substitute_teacher_id: string }[] = await weeklyAssignResponse.json()
    for (const row of rows) {
      const tid = row.substitute_teacher_id
      weeklyPostedDraftCounts.set(tid, (weeklyPostedDraftCounts.get(tid) ?? 0) + 1)
    }
  }

  // Batch fetch: timetable period counts per teacher on this dayOfWeek (for min_free_periods)
  const ttCountParams = new URLSearchParams()
  ttCountParams.append('select', 'teacher_id')
  ttCountParams.append('day_of_week', `eq.${dayOfWeek}`)
  if (publishedYearIds.length > 0) {
    ttCountParams.append('academic_year_id', `in.(${publishedYearIds.join(',')})`)
  }

  const ttCountResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/timetable_records?${ttCountParams}`,
    { headers }
  )

  const teacherPeriodCounts = new Map<string, number>()
  if (ttCountResponse.ok) {
    const rows: { teacher_id: string }[] = await ttCountResponse.json()
    for (const row of rows) {
      teacherPeriodCounts.set(
        row.teacher_id,
        (teacherPeriodCounts.get(row.teacher_id) ?? 0) + 1
      )
    }
  }

  // Apply rules 5, 6, 7 per candidate
  const beforeQuota = candidates.length
  candidates = candidates.filter((teacher) => {
    const tid = teacher.id
    const batchCounts = draftBatchCounts.get(tid) ?? { daily: 0, weekly: 0 }

    // Rule 5: Daily cap
    const dailyCount =
      (dailyPostedDraftCounts.get(tid) ?? 0) + batchCounts.daily
    if (dailyCount + 1 > rules.max_subs_per_day) return false

    // Rule 6: Weekly cap
    const weeklyCount =
      (weeklyPostedDraftCounts.get(tid) ?? 0) + batchCounts.weekly
    if (weeklyCount + 1 > rules.max_subs_per_week) return false

    // Rule 7: Minimum free periods
    const totalPeriods =
      teacherPeriodCounts.get(tid) ?? rules.total_periods_per_day
    const existingAssignments = dailyCount
    const freePeriods = totalPeriods - existingAssignments
    if (freePeriods - 1 < rules.min_free_periods) return false

    return true
  })
  const quotaExceededCount = beforeQuota - candidates.length

  return {
    candidates,
    breakdown: {
      totalTeachers,
      absentCount,
      unavailableCount,
      busyCount,
      quotaExceededCount,
      ineligibleTypeCount,
      availableCount: candidates.length,
    },
  }
}
